/**
 * components/ResultsTable.jsx
 * Displays inspection plan results in a table format.
 * Supports an inline edit mode: edit cells, add rows, delete rows, save to backend.
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import API from '../services/api';
import BlockTolerancesSection from './BlockTolerancesSection';
import { splitVisualFromNotes } from '../utils/visualSplit';
import { fmtTolPlus, fmtTolMinus } from '../utils/tolerance';
import '../styles/components.css';

// Map spec_category keys → display labels
const CAT_LABELS = {
  linear_dimensions: 'Linear Dimensions',
  angular_dimensions: 'Angular Dimensions',
  circular_radial_dimensions: 'Circular / Radial',
  chamfer_bevel_dimensions: 'Chamfer & Bevel',
  coordinate_dimensions: 'Coordinate Dims',
  thread_dimensions: 'Thread Dimensions',
  surface_dimensions: 'Surface Dims',
  taper_slope_dimensions: 'Taper & Slope',
  gdt_feature_control: 'GD&T',
  surface_finish_cosmetic: 'Surface Finish',
  material_metallurgy: 'Material & Metallurgy',
  manufacturing_process: 'Mfg. Process',
  assembly_fit: 'Assembly & Fit',
  inspection_quality: 'Inspection Quality',
  regulatory_compliance: 'Regulatory',
  environmental_durability: 'Environmental',
};

// Full category/sub-type/tool data (mirrors CATEGORIES in backend/routers/business_rules.py)
const CATEGORIES_DATA = [
  {
    key: 'linear_dimensions', label: 'Linear Dimensions', spec_type: 'dimensional',
    sub_types: ['Horizontal dimensions', 'Vertical dimensions', 'Aligned dimensions', 'Offset dimensions'],
    tools: ['Steel Ruler', 'Vernier Caliper', 'Micrometer', 'Gauge', 'Dial Indicator', 'CMM', 'Laser Tools'],
  },
  {
    key: 'angular_dimensions', label: 'Angular Dimensions', spec_type: 'dimensional',
    sub_types: ['Included angle', 'Chamfer angle', 'Taper angle'],
    tools: ['Protractor', 'Bevel Protractor', 'Combination Square', 'Angle Gauge Blocks', 'Digital Angle Finder', 'Sine Bar / Sine Plate', 'CMM'],
  },
  {
    key: 'circular_radial_dimensions', label: 'Circular / Radial Dimensions', spec_type: 'dimensional',
    sub_types: ['Diameter', 'Radius', 'Counterbore', 'Countersink', 'Spotface', 'Chamfer diameter', 'Circular runout / Total runout', 'Arc'],
    tools: ['Caliper', 'Micrometer', 'Gauge', 'Dial Indicator', 'CMM', 'Radius Gauge'],
  },
  {
    key: 'chamfer_bevel_dimensions', label: 'Chamfer & Bevel Dimensions', spec_type: 'dimensional',
    sub_types: ['Chamfer angle', 'Chamfer length', 'Bevel angle', 'Bevel width'],
    tools: ['Bevel Protractor', 'Caliper', 'Micrometer', 'Angle Gauge Blocks', 'CMM'],
  },
  {
    key: 'coordinate_dimensions', label: 'Coordinate Dimensions', spec_type: 'dimensional',
    sub_types: ['Cartesian (X, Y, Z)', 'Polar coordinates (R, theta)'],
    tools: ['Caliper', 'Height Gauge', 'CMM', 'Surface Plate with Gauge Blocks'],
  },
  {
    key: 'thread_dimensions', label: 'Thread Dimensions', spec_type: 'dimensional',
    sub_types: ['Metric thread', 'Unified thread', 'Thread depth', 'Fit Class'],
    tools: ['Thread Gauge (GO/NO-GO)', 'Pitch Gauge', 'Micrometer', 'Optical Comparator', 'CMM'],
  },
  {
    key: 'surface_dimensions', label: 'Surface Dimensions', spec_type: 'dimensional',
    sub_types: ['Surface roughness', 'Lay direction', 'Surface waviness'],
    tools: ['Surface Roughness Tester', 'Comparator Plates', 'Optical Comparator', 'CMM', 'Dial Indicator'],
  },
  {
    key: 'taper_slope_dimensions', label: 'Taper & Slope Dimensions', spec_type: 'dimensional',
    sub_types: ['Taper ratio', 'Slope'],
    tools: ['Sine Bar', 'Sine Plate', 'Bevel Protractor', 'Dial Indicator', 'CMM'],
  },
  {
    key: 'gdt_feature_control', label: 'GD&T / Feature Control', spec_type: 'dimensional',
    sub_types: ['Flatness', 'Straightness', 'Circularity', 'Cylindricity', 'Perpendicularity', 'Parallelism', 'Angularity', 'Position', 'Concentricity', 'Symmetry', 'Circular Runout', 'Total Runout'],
    tools: ['CMM', 'Dial Indicator', 'Optical Comparator', 'Surface Plate'],
  },
  {
    key: 'surface_finish_cosmetic', label: 'Surface Finish & Cosmetic', spec_type: 'non_dimensional',
    sub_types: ['Surface roughness', 'Cosmetic class (A/B surface)', 'Paint / powder coat specification', 'No scratches', 'Burr free edges', 'Polishing requirements'],
    tools: ['Surface Roughness Tester', 'Comparator Plates', 'Visual Inspection', 'Thickness Gauge'],
  },
  {
    key: 'material_metallurgy', label: 'Material & Metallurgy', spec_type: 'non_dimensional',
    sub_types: ['Material grade', 'Heat treatment', 'Temper condition', 'Grain direction', 'Coating thickness', 'Surface hardness'],
    tools: ['Hardness Tester', 'Lab Analysis', 'Certificate Review', 'Thickness Gauge', 'Micrometer'],
  },
  {
    key: 'manufacturing_process', label: 'Manufacturing Process Req.', spec_type: 'non_dimensional',
    sub_types: ['Machine all surfaces', 'Cast and clean', 'Laser cut edges', 'No welding', 'Form before heat treat', 'Break all sharp edges'],
    tools: ['Visual Inspection', 'Process Audit', 'Caliper', 'Burr Gauge'],
  },
  {
    key: 'assembly_fit', label: 'Assembly & Fit Req.', spec_type: 'non_dimensional',
    sub_types: ['Press fit', 'Apply Loctite on threads', 'Install gasket', 'Torque bolts'],
    tools: ['Torque Wrench', 'Go/No-Go Gauge', 'Force Gauge', 'Dial Indicator', 'Visual Inspection'],
  },
  {
    key: 'inspection_quality', label: 'Inspection & Quality Req.', spec_type: 'non_dimensional',
    sub_types: ['Critical characteristic — inspect 100%', 'CMM for positional tolerances', 'Surface finish measurement', 'Go/No-Go gauge required', 'Sample size per ANSI Z1.4'],
    tools: ['CMM', 'Caliper', 'Micrometer', 'Go/No-Go Gauge', 'Surface Roughness Tester'],
  },
  {
    key: 'regulatory_compliance', label: 'Regulatory Compliance', spec_type: 'non_dimensional',
    sub_types: ['RoHS compliant', 'REACH compliant', 'Biocompatibility per ISO 10993', 'Lot traceability mandatory'],
    tools: ['Certificate Review', 'Lab Analysis', 'Records Review'],
  },
  {
    key: 'environmental_durability', label: 'Environmental & Durability', spec_type: 'non_dimensional',
    sub_types: ['Corrosion resistance', 'Storage temperature range', 'UV resistance', 'Chemical resistance'],
    tools: ['Salt Spray Tester', 'UV Test Chamber', 'Visual Inspection', 'Lab Testing'],
  },
];
const CAT_DATA_MAP = Object.fromEntries(CATEGORIES_DATA.map(c => [c.key, c]));

// Colour coding per broad group
function typeBg(specCategory, inspectionType) {
  if (!specCategory) {
    return inspectionType === 'Dimensional'
      ? { bg: '#eff6ff', text: '#1d4ed8' }
      : { bg: '#f0fdf4', text: '#15803d' };
  }
  const dim = [
    'linear_dimensions', 'angular_dimensions', 'circular_radial_dimensions',
    'chamfer_bevel_dimensions', 'coordinate_dimensions', 'thread_dimensions',
    'surface_dimensions', 'taper_slope_dimensions', 'gdt_feature_control',
  ];
  if (dim.includes(specCategory)) return { bg: '#eff6ff', text: '#1d4ed8' };
  return { bg: '#fdf4ff', text: '#7c3aed' };
}

/** Human-readable specification category (matches Excel “Specification Category” column). */
function specCategoryDisplay(item) {
  const cat = item.spec_category || '';
  const sub = item.spec_sub_type || '';
  const label = cat ? (CAT_LABELS[cat] || cat) : '';
  if (label && sub) return `${label} — ${sub}`;
  return label || sub || '—';
}

function SpecificationTypeCell({ item }) {
  const { bg, text } = typeBg(item.spec_category, item.inspection_type);
  const t = item.inspection_type || '—';
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, fontWeight: 700,
      padding: '2px 8px', borderRadius: 4,
      background: bg, color: text, whiteSpace: 'nowrap',
    }}>
      {t}
    </span>
  );
}

const BLANK_ROW = () => ({
  _tempId: Math.random().toString(36).slice(2),
  location_on_drawing: '',
  critical_characteristic: 'Yes',
  specification: '',
  nominal: '',
  tolerance_plus: '',
  tolerance_minus: '',
  ucl: '',
  lcl: '',
  inspection_type: 'Dimensional',
  spec_category: '',
  spec_sub_type: '',
  inspection_tool: '',
});

const INPUT_STYLE = {
  width: '100%', padding: '4px 6px', border: '1px solid #cbd5e1',
  borderRadius: 4, fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box',
  background: '#fff',
};

const SELECT_STYLE = { ...INPUT_STYLE, cursor: 'pointer' };

const BLANK_NOTE_ROW = () => ({
  _tempId: Math.random().toString(36).slice(2),
  category: 'General Note',
  note_label: '',
  value_detail: '',
  source_on_drawing: '',
  needs_review: false,
});

const BLANK_GDT_ROW = () => ({
  _tempId: Math.random().toString(36).slice(2),
  gdt_item_no: '',
  location: '',
  spec_sub_type: '',
  ucl: '',
  datum_references: '',
  critical_characteristic: 'No',
});

const GDT_CONTROL_TYPES = [
  'Flatness', 'Straightness', 'Circularity', 'Cylindricity',
  'Profile of a Line', 'Profile of a Surface',
  'Perpendicularity', 'Parallelism', 'Angularity', 'Position',
  'Concentricity', 'Symmetry', 'Circular Runout', 'Total Runout',
];

// Symbol options for the GD&T Symbol dropdown in edit mode
// Each entry: { label, value } — value stored in row.gdt_symbol, displayed in Symbol column
const GDT_SYMBOL_OPTIONS = [
  { label: '— (none / other)', value: '' },
  { label: '⏤  Straightness',         value: '⏤' },
  { label: '⏥  Flatness',             value: '⏥' },
  { label: '○  Circularity',          value: '○' },
  { label: '⌭  Cylindricity',         value: '⌭' },
  { label: '⌒  Profile of a Line',    value: '⌒' },
  { label: '⌓  Profile of a Surface', value: '⌓' },
  { label: '∠  Angularity',           value: '∠' },
  { label: '⟂  Perpendicularity',     value: '⟂' },
  { label: '∥  Parallelism',          value: '∥' },
  { label: '⌖  Position',             value: '⌖' },
  { label: '◎  Concentricity',        value: '◎' },
  { label: '⌯  Symmetry',             value: '⌯' },
  { label: '↗  Circular Runout',      value: '↗' },
  { label: '⌰  Total Runout',         value: '⌰' },
];

// Patterns that indicate a visual inspection item is just noise (standard refs, zone markers)
const _NOISE_LABEL_RE = [
  /^Y14\./i,                          // ASME Y14.x drawing standards
  /^ASME\s/i,                         // ASME references
  /^ISO\s+\d/i,                       // ISO standards
  /^ANSI\s/i,
  /^MIL-/i,
  /^BS\s+\d/i,
  /^[A-Z]{0,2}\d{1,2}[A-Z]?$/,       // zone markers like "2R", "A3", "B4"
  /^DRG\s*NO/i,                       // drawing number labels
  /^\d{4}-\d{4}-\d{2}$/,             // date-like codes
];

function _isNoisyVisualLabel(label) {
  const s = String(label || '').trim();
  if (!s || s === '—') return true;
  return _NOISE_LABEL_RE.some(re => re.test(s));
}

// If note_label looks like a bare surface finish Ra value, reformat it
function _reformatVisualLabel(label, source) {
  const s = String(label || '').trim();
  // pure integer that is a standard Ra value (16, 32, 63, 125, 250, 500…)
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    const raValues = [1, 2, 4, 8, 16, 32, 63, 125, 250, 500, 1000, 2000];
    if (raValues.includes(n) || (n >= 1 && n <= 2000)) {
      return { label: `Surface Texture Ra µin: ${s}`, category: 'Finish & Edge' };
    }
  }
  // "Ra 32" or "32 µin" patterns
  if (/^Ra\s*\d+/i.test(s) || /^\d+\s*µin/i.test(s)) {
    return { label: `Surface Texture ${s}`, category: 'Finish & Edge' };
  }
  return null;
}

const SPEC_CAT_TO_NOTE_CAT = {
  material_metallurgy:     'Material',
  thread_dimensions:       'Thread',
  surface_finish_cosmetic: 'Finish & Edge',
  surface_dimensions:      'Finish & Edge',
  regulatory_compliance:   'Compliance',
  manufacturing_process:   'General Note',
  assembly_fit:            'General Note',
  inspection_quality:      'General Note',
  environmental_durability:'General Note',
};

function autoNoteFields(row) {
  const category = SPEC_CAT_TO_NOTE_CAT[row.spec_category] || 'General Note';
  const note_label = row.specification || '';
  const parts = [];
  if (row.nominal != null && row.nominal !== '') parts.push(`Nominal: ${row.nominal}`);
  if (row.tolerance_plus || row.tolerance_minus) {
    const tol = [
      row.tolerance_plus  ? fmtTolPlus(row.tolerance_plus)   : '',
      row.tolerance_minus ? fmtTolMinus(row.tolerance_minus) : '',
    ].filter(Boolean).join(' / ');
    parts.push(`Tolerance: ${tol}`);
  }
  return {
    category,
    note_label,
    value_detail: parts.join(', '),
    verification_method: 'Visual inspection',
    responsible_role: 'QC Inspector',
  };
}

function EditCell({ value, onChange, type = 'text', options }) {
  if (options) {
    return (
      <select value={value || ''} onChange={e => onChange(e.target.value)} style={SELECT_STYLE}>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      style={INPUT_STYLE}
    />
  );
}

// Hazard category → colour mapping
// Legacy hazard colors (kept for backward compat)
const HAZARD_COLORS = {
  Mechanical:  { bg: '#fee2e2', text: '#991b1b' },
  Thermal:     { bg: '#ffedd5', text: '#9a3412' },
  Chemical:    { bg: '#fef9c3', text: '#854d0e' },
  Ergonomic:   { bg: '#ede9fe', text: '#6d28d9' },
  Electrical:  { bg: '#dbeafe', text: '#1e40af' },
  Pressure:    { bg: '#dcfce7', text: '#166534' },
};

// 11-category Notes Table colour map (hex background)
const NOTE_CAT_COLORS = {
  'Material':         '#DBEAFE',
  'Finish & Edge':    '#DCFCE7',
  'Cleanliness':      '#DCFCE7',
  'Cosmetic':         '#FCE7F3',
  'Compliance':       '#FEF3C7',
  'Thread':           '#E0E7FF',
  'Drawing Standard': '#F3F4F6',
  'Documentation':    '#F3F4F6',
  'General Note':     '#F3F4F6',
  'Safety / Hazard':  '#FEE2E2',
  'Revision':         '#F3F4F6',
};

// Text colour per category (dark enough to read on the bg)
const NOTE_CAT_TEXT = {
  'Material':         '#1e40af',
  'Finish & Edge':    '#166534',
  'Cleanliness':      '#166534',
  'Cosmetic':         '#9d174d',
  'Compliance':       '#92400e',
  'Thread':           '#3730a3',
  'Drawing Standard': '#374151',
  'Documentation':    '#374151',
  'General Note':     '#374151',
  'Safety / Hazard':  '#991b1b',
  'Revision':         '#374151',
};

const NOTE_CATEGORIES = Object.keys(NOTE_CAT_COLORS);

/**
 * Visual Inspection Section — pass / fail visual checks routed out of the
 * Notes Table. Renders between the main dimensional table and the
 * Notes Section. No tolerance / UCL / LCL columns.
 */
function VisualInspectionSection({ items }) {
  const rows = Array.isArray(items) ? items : [];
  const count = rows.length;

  const hdrs = [
    '#', 'Specification', 'Source on Drawing',
    'Inspection Type', 'Inspection Tool', 'Responsible Role', 'Remarks',
  ];

  return (
    <div style={{ marginTop: 32 }} id="visual-inspection-section">
      {/* Section header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
        borderLeft: '4px solid #0EA5E9', paddingLeft: 12,
      }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#0EA5E9' }}>&#128065;</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#0C4A6E' }}>
            Visual Inspection ({count})
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {count > 0
              ? `${count} visual inspection check${count !== 1 ? 's' : ''} routed from drawing notes (no tolerance / UCL / LCL)`
              : 'No visual inspection items detected on this drawing'}
          </div>
        </div>
      </div>

      {count === 0 ? (
        <div style={{
          padding: '14px 18px',
          background: '#F0F9FF',
          border: '1px solid #BAE6FD',
          borderRadius: 8,
          fontSize: 13,
          color: '#0C4A6E',
          fontStyle: 'italic',
        }}>
          No visual inspection items were detected on this drawing.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #BAE6FD' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#0C4A6E' }}>
                {hdrs.map(h => (
                  <th key={h} style={{
                    padding: '9px 12px', textAlign: 'left', fontWeight: 600,
                    fontSize: 11, color: '#fff', whiteSpace: 'nowrap',
                    borderBottom: '2px solid #0C4A6E',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((v, i) => {
                const bg = v.needs_review
                  ? '#FFF3CD'
                  : (i % 2 === 0 ? '#fff' : '#F0F9FF');
                return (
                  <tr key={i} style={{ background: bg, borderBottom: '1px solid #e0f2fe' }}>
                    <td style={{ padding: '8px 10px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {v.item_no || i + 1}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#0f172a', maxWidth: 480, lineHeight: 1.45 }}>
                      {v.specification || v.note_label || '—'}
                      {v.needs_review && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: '#B45309', fontStyle: 'italic' }}>
                          ⚑ needs review
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#475569', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                      {v.source_on_drawing || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px',
                        borderRadius: 4, fontSize: 11, fontWeight: 700,
                        background: '#E0F2FE', color: '#0C4A6E',
                        border: '1px solid #BAE6FD',
                      }}>
                        {v.inspection_type || 'Visual'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', color: '#1e293b', whiteSpace: 'nowrap' }}>
                      {v.inspection_tool || 'Visual'}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#475569', whiteSpace: 'nowrap' }}>
                      {v.responsible_role || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#6b7280', fontStyle: 'italic', maxWidth: 220 }}>
                      {v.remarks || ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Notes Section — 11-category non-dimensional information extracted from the drawing.
 * Replaces SafetyHazardSection (v1.6).
 */
function NotesSection({ items }) {
  const allRows  = Array.isArray(items) ? items : [];
  const [catFilter, setCatFilter] = React.useState('All');

  const countByCat = React.useMemo(() => {
    const c = { All: allRows.length };
    NOTE_CATEGORIES.forEach(cat => {
      c[cat] = allRows.filter(r => r.category === cat).length;
    });
    return c;
  }, [allRows]);

  const visible = catFilter === 'All'
    ? allRows
    : allRows.filter(r => r.category === catFilter);

  return (
    <div style={{ marginTop: 32 }} id="notes-section">
      {/* Section header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
        borderLeft: '4px solid #F97316', paddingLeft: 12,
      }}>
        <span style={{ fontSize: 18 }}>&#128221;</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#c2410c' }}>
            Notes ({allRows.length})
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {allRows.length > 0
              ? `${allRows.length} non-dimensional note${allRows.length !== 1 ? 's' : ''} extracted from the drawing`
              : 'No notes detected on this drawing'}
          </div>
        </div>
      </div>

      {/* Category filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {['All', ...NOTE_CATEGORIES].map(cat => {
          const cnt = countByCat[cat] || 0;
          if (cat !== 'All' && cnt === 0) return null;
          const active = catFilter === cat;
          const bg  = cat === 'All' ? (active ? '#F97316' : '#f3f4f6') : (active ? NOTE_CAT_COLORS[cat] : '#f9fafb');
          const col = cat === 'All' ? (active ? '#fff' : '#374151') : (active ? NOTE_CAT_TEXT[cat] : '#6b7280');
          return (
            <button
              key={cat}
              onClick={() => setCatFilter(cat)}
              style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                border: active ? '2px solid ' + (cat === 'All' ? '#F97316' : NOTE_CAT_TEXT[cat]) : '1px solid #e5e7eb',
                background: bg, color: col, cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {cat} {cnt > 0 ? `(${cnt})` : ''}
            </button>
          );
        })}
      </div>

      {allRows.length === 0 ? (
        <div style={{
          padding: '14px 18px', background: '#FFFBEB', border: '1px solid #FDE68A',
          borderRadius: 8, fontSize: 13, color: '#92400E', fontStyle: 'italic',
        }}>
          No notes were detected on this drawing. If notes apply, please re-scan with
          an updated drawing or add them manually.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #fdba74' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#1F2937' }}>
                {['Category', 'Note / Specification', 'Value / Detail', 'Source on Drawing'].map(h => (
                  <th key={h} style={{
                    padding: '9px 12px', textAlign: 'left', fontWeight: 600,
                    fontSize: 11, color: '#fff', whiteSpace: 'nowrap',
                    borderBottom: '2px solid #374151',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((n, i) => {
                const cat = n.category || 'General Note';
                const rowBg = n.needs_review ? '#FFF3CD' : (NOTE_CAT_COLORS[cat] || '#F3F4F6');
                const catBg = NOTE_CAT_COLORS[cat] || '#F3F4F6';
                const catTxt = NOTE_CAT_TEXT[cat] || '#374151';
                const isThread  = cat === 'Thread';
                const isSafety  = cat === 'Safety / Hazard';
                return (
                  <tr key={i} style={{ background: rowBg, borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                        background: catBg, color: catTxt, border: '1px solid rgba(0,0,0,0.08)',
                      }}>
                        {isThread && <span>🔧</span>}
                        {isSafety && <span style={{ color: '#dc2626' }}>⚠</span>}
                        {cat}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', fontWeight: 500, color: '#1e293b' }}>
                      {n.note_label || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#1e293b', maxWidth: 260 }}>
                      {n.value_detail || '—'}
                      {n.needs_review && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: '#B45309', fontStyle: 'italic' }}>
                          ⚑ needs review
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#475569', fontStyle: 'italic' }}>
                      {n.source_on_drawing || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Backward-compat alias
function SafetyHazardSection({ items }) {
  return <NotesSection items={items} />;
}

/**
 * Combined Notes and Visual Inspection section.
 * Visual rows appear first (light-blue background), then notes rows.
 */
function NotesAndVisualSection({ visualItems, notesItems, onSaveNotes, onDemoteNote }) {
  const visRows   = Array.isArray(visualItems) ? visualItems : [];
  const notesRows = Array.isArray(notesItems)  ? notesItems  : [];

  // Map + clean visual rows: filter noise, reformat surface texture values
  const mappedVis = visRows
    .map((v, i) => {
      const rawLabel = String(v.specification || v.note_label || '').trim();
      if (_isNoisyVisualLabel(rawLabel)) return null;  // drop noise
      const reformatted = _reformatVisualLabel(rawLabel, v.source_on_drawing);
      return {
        _tempId:             Math.random().toString(36).slice(2),
        item_no:             v.item_no || i + 1,
        category:            reformatted ? reformatted.category : 'Visual Inspection',
        note_label:          reformatted ? reformatted.label : rawLabel || '—',
        value_detail:        v.value_detail || '',
        source_on_drawing:   v.source_on_drawing || '—',
        verification_method: v.inspection_tool || v.verification_method || 'Visual',
        responsible_role:    v.responsible_role || 'Inspector',
        remarks:             v.remarks || '',
        needs_review:        v.needs_review || false,
        _isVisual:           true,
      };
    })
    .filter(Boolean);

  // NOTE: the catch-all "Perform visual inspection under adequate lighting…"
  // row is seeded by the backend exactly once (and stays deleted if the user
  // removes it) — do NOT inject it here, or user deletions can never stick.

  // Edit mode — ALL rows (visual + notes) are editable; saved together via onSaveNotes
  const [editMode, setEditMode] = React.useState(false);
  const [editedRows, setEditedRows] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);
  const [demotingNote, setDemotingNote] = React.useState(null); // key = note_label+category

  const enterEditMode = () => {
    // Merge visual rows first, then notes rows — all become editable
    setEditedRows([...mappedVis.map(r => ({ ...r })), ...notesRows.map(r => ({ ...r }))]);
    setSaveError(null);
    setEditMode(true);
  };
  const cancelEdit = () => { setEditMode(false); setSaveError(null); };

  const updateCell = (idx, field, value) =>
    setEditedRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const addNoteRow = () => setEditedRows(prev => [...prev, BLANK_NOTE_ROW()]);
  const duplicateNoteRow = (idx) => setEditedRows(prev => {
    const next = [...prev];
    next.splice(idx + 1, 0, { ...prev[idx], _tempId: Math.random().toString(36).slice(2) });
    return next;
  });
  const deleteRow  = (idx) => setEditedRows(prev => prev.filter((_, i) => i !== idx));

  const demoteNoteRow = async (row) => {
    if (!onDemoteNote) return;
    const key = (row.note_label || '') + '||' + (row.category || '');
    setDemotingNote(key);
    setSaveError(null);
    try {
      await onDemoteNote(row.note_label || '', row.category || '');
      setEditedRows(prev => prev.filter(r => !((r.note_label || '') === (row.note_label || '') && (r.category || '') === (row.category || ''))));
    } catch (e) {
      setSaveError(e?.error || e?.message || 'Failed to move note to Dimensions for Review');
    } finally { setDemotingNote(null); }
  };

  const saveNotes = async () => {
    if (!onSaveNotes) return;
    setSaving(true); setSaveError(null);
    try {
      // Strip internal helpers before saving
      const toSave = editedRows.map(({ _isVisual, _tempId, item_no, ...rest }) => rest);
      await onSaveNotes(toSave);
      setEditMode(false);
    } catch (e) {
      setSaveError(e?.error || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const activeNotes = editMode ? editedRows : [...mappedVis, ...notesRows];
  const allRows = activeNotes;
  const total   = allRows.length;

  const [catFilter, setCatFilter] = React.useState('All');
  const cats = ['All', 'Visual Inspection', ...NOTE_CATEGORIES];

  const countByCat = React.useMemo(() => {
    const c = { All: allRows.length, 'Visual Inspection': 0 };
    NOTE_CATEGORIES.forEach(cat => { c[cat] = 0; });
    allRows.forEach(r => {
      const cat = r.category || 'General Note';
      if (cat === 'Visual Inspection') c['Visual Inspection'] = (c['Visual Inspection'] || 0) + 1;
      else if (c[cat] !== undefined) c[cat]++;
    });
    return c;
  }, [allRows]);

  const visible = editMode
    ? allRows
    : catFilter === 'All'
      ? allRows
      : allRows.filter(r => (r.category || 'General Note') === catFilter);

  const notesBtnBase = { padding: '5px 12px', borderRadius: 5, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12 };
  const hdrs = ['Category', 'Note / Specification', 'Value / Detail', 'Source on Drawing'];

  return (
    <div style={{ marginTop: 32 }} id="notes-visual-section">
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, marginBottom: 12, borderLeft: '4px solid #4A7C59', paddingLeft: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>&#128221;</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#4A7C59' }}>
              Notes and Visual Inspection ({total})
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              {allRows.filter(r => r.category === 'Visual Inspection').length} visual checks &nbsp;·&nbsp; {allRows.filter(r => r.category !== 'Visual Inspection').length} notes
            </div>
          </div>
        </div>
        {onSaveNotes && !editMode && (
          <button style={{ ...notesBtnBase, background: '#2E75B6', color: '#fff' }} onClick={enterEditMode}>
            ✎ Edit All Rows
          </button>
        )}
        {editMode && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...notesBtnBase, background: '#16a34a', color: '#fff' }} onClick={saveNotes} disabled={saving}>
              {saving ? 'Saving…' : '✓ Save'}
            </button>
            <button style={{ ...notesBtnBase, background: '#1F3864', color: '#fff' }} onClick={addNoteRow} disabled={saving}>
              + Add Note
            </button>
            <button style={{ ...notesBtnBase, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }} onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {saveError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 14px', color: '#dc2626', fontSize: 13, marginBottom: 8 }}>
          {saveError}
        </div>
      )}

      {/* Category filter — hidden in edit mode */}
      {!editMode && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {cats.map(cat => {
            const cnt    = countByCat[cat] || 0;
            if (cat !== 'All' && cnt === 0) return null;
            const active = catFilter === cat;
            const bg     = active
              ? (cat === 'Visual Inspection' ? '#0EA5E9' : cat === 'All' ? '#4A7C59' : (NOTE_CAT_COLORS[cat] || '#374151'))
              : '#f3f4f6';
            const col    = active ? '#fff' : '#374151';
            return (
              <button key={cat} onClick={() => setCatFilter(cat)} style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                border: active ? `2px solid ${bg}` : '1px solid #e5e7eb',
                background: bg, color: col, cursor: 'pointer', transition: 'all 0.15s',
              }}>{cat} ({cnt})</button>
            );
          })}
        </div>
      )}

      {total === 0 ? (
        <div style={{ padding: '14px 18px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, fontSize: 13, color: '#166534', fontStyle: 'italic' }}>
          No notes or visual inspection items detected on this drawing.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #A3C4A8' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#1F2937' }}>
                {editMode && <th style={{ width: 36, padding: '9px 6px', borderBottom: '2px solid #374151' }}></th>}
                {hdrs.map(h => (
                  <th key={h} style={{
                    padding: '9px 12px', textAlign: 'left', fontWeight: 600,
                    fontSize: 11, color: '#fff', whiteSpace: 'nowrap',
                    borderBottom: '2px solid #374151',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row, i) => {
                const cat    = row.category || 'General Note';
                const isVis  = cat === 'Visual Inspection';
                const rowBg  = row.needs_review
                  ? '#FFF3CD'
                  : isVis ? (i % 2 === 0 ? '#E0F2FE' : '#F0F9FF')
                  : (NOTE_CAT_COLORS[cat] || '#F3F4F6');
                const catBg  = isVis ? '#E0F2FE' : (NOTE_CAT_COLORS[cat] || '#F3F4F6');
                const catTxt = isVis ? '#0C4A6E' : (NOTE_CAT_TEXT[cat] || '#374151');

                // Find the index of this row in the full editedRows array for updates
                const editIdx = editMode ? allRows.indexOf(row) : -1;

                if (editMode) {
                  const demoteKey = (row.note_label || '') + '||' + (row.category || '');
                  const isDemoting = demotingNote === demoteKey;
                  return (
                    <tr key={row._tempId || i} style={{ background: rowBg, borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button onClick={() => deleteRow(editIdx)} style={{ background: '#fee2e2', border: 'none', borderRadius: 3, color: '#dc2626', cursor: 'pointer', fontWeight: 700, padding: '2px 7px', fontSize: 13 }}>✕</button>
                        <button onClick={() => duplicateNoteRow(editIdx)} title="Duplicate note" style={{ marginLeft: 4, background: '#dbeafe', border: 'none', borderRadius: 3, color: '#1d4ed8', cursor: 'pointer', fontWeight: 700, padding: '2px 7px', fontSize: 13 }}>⧉</button>
                        {onDemoteNote && !isVis && (
                          <button
                            onClick={() => demoteNoteRow(row)}
                            disabled={isDemoting}
                            title="Move this note to Dimensions for Review"
                            style={{ marginLeft: 4, background: '#FEF3C7', border: 'none', borderRadius: 3, color: '#B45309', cursor: 'pointer', fontWeight: 700, padding: '2px 7px', fontSize: 11, opacity: isDemoting ? 0.6 : 1 }}
                          >
                            {isDemoting ? '…' : '→ Review'}
                          </button>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={row.category || 'Visual Inspection'} onChange={e => updateCell(editIdx, 'category', e.target.value)} style={SELECT_STYLE}>
                          <option value="Visual Inspection">👁 Visual Inspection</option>
                          {NOTE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={row.note_label || ''} onChange={e => updateCell(editIdx, 'note_label', e.target.value)} style={INPUT_STYLE} placeholder="Note / specification text" />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={row.value_detail || ''} onChange={e => updateCell(editIdx, 'value_detail', e.target.value)} style={INPUT_STYLE} placeholder="Value or detail" />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={row.source_on_drawing || ''} onChange={e => updateCell(editIdx, 'source_on_drawing', e.target.value)} style={INPUT_STYLE} placeholder="Source on drawing" />
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={row._tempId || i} style={{ background: rowBg, borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                        background: catBg, color: catTxt, border: '1px solid rgba(0,0,0,0.08)',
                      }}>
                        {isVis && <span>👁</span>}
                        {cat === 'Thread' && <span>🔧</span>}
                        {cat === 'Safety / Hazard' && <span style={{ color: '#dc2626' }}>⚠</span>}
                        {cat}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', fontWeight: 500, color: '#1e293b', maxWidth: 400 }}>
                      {row.note_label || '—'}
                      {row.needs_review && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: '#B45309', fontStyle: 'italic' }}>
                          ⚑ needs review
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#1e293b', maxWidth: 220 }}>
                      {row.value_detail || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#475569', fontStyle: 'italic' }}>
                      {row.source_on_drawing || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const GDT_GLYPH_MAP = {
  'straightness':          '\u23E4',
  'flatness':              '\u23E5',
  'circularity':           '\u25CB',
  'cylindricity':          '\u232D',
  'profile of a line':     '\u2312',
  'profile of a surface':  '\u2313',
  'angularity':            '\u2220',
  'perpendicularity':      '\u27C2',
  'parallelism':           '\u2225',
  'position':              '\u2316',
  'true position':         '\u2316',
  'concentricity':         '\u25CE',
  'symmetry':              '\u232F',
  'circular runout':       '\u2197',
  'total runout':          '\u2330',
  'roundness':             '\u25CB',
  'runout':                '\u2197',
  'coaxiality':            '\u25CE',
};

function resolveGlyph(row) {
  // Explicit symbol set by user in edit mode takes highest priority
  if (row.gdt_symbol) return row.gdt_symbol;
  if (row.symbol_glyph) return row.symbol_glyph;
  const name = (row.spec_sub_type || '').toLowerCase().trim();
  if (!name) return '';
  const direct = GDT_GLYPH_MAP[name];
  if (direct) return direct;
  for (const [key, glyph] of Object.entries(GDT_GLYPH_MAP)) {
    if (name.includes(key) || key.includes(name)) return glyph;
  }
  return '';
}

/**
 * Pull datum letters from strings like "{'letter': 'A', 'modifier': None}, …"
 * (bad serialization) so the table shows "A, B, C".
 */
function lettersFromDatumString(s) {
  if (typeof s !== 'string' || !s.trim()) return [];
  const re = /['"]letter['"]\s*:\s*['"]([A-Za-z])['"]/g;
  return [...s.matchAll(re)].map((m) => m[1].toUpperCase());
}

/**
 * GD&T Table — always visible below the Inspection Plan.
 * Supports inline edit mode when onSaveGdt callback is provided.
 */
function GdtTableSection({ gdtRows, onSaveGdt, onDemoteGdt }) {
  const rawRows = (Array.isArray(gdtRows) ? gdtRows : []).filter(
    (r) => r && !r.is_block_tolerance_row && r.row_type !== 'block_tolerance',
  );

  const [editMode, setEditMode] = useState(false);
  const [editedRows, setEditedRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [demotingGdt, setDemotingGdt] = useState(null); // gdt_item_no being demoted

  const enterEditMode = () => {
    setEditedRows(rawRows.map(r => ({ ...r })));
    setSaveError(null);
    setEditMode(true);
  };
  const cancelEdit = () => { setEditMode(false); setSaveError(null); };
  const updateRow = (idx, field, value) =>
    setEditedRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const addGdtRow = () => setEditedRows(prev => [...prev, BLANK_GDT_ROW()]);
  const deleteGdtRow = (idx) => setEditedRows(prev => prev.filter((_, i) => i !== idx));
  const duplicateGdtRow = (idx) => setEditedRows(prev => {
    const next = [...prev];
    next.splice(idx + 1, 0, { ...prev[idx], _tempId: Math.random().toString(36).slice(2) });
    return next;
  });

  const demoteGdtRow = async (row, idx) => {
    if (!onDemoteGdt) return;
    // Use the 1-based item number from the saved state (idx+1 if not set)
    const itemNo = row.gdt_item_no || idx + 1;
    setDemotingGdt(itemNo);
    setSaveError(null);
    try {
      await onDemoteGdt(itemNo);
      setEditedRows(prev => prev.filter((_, i) => i !== idx));
    } catch (e) {
      setSaveError(e?.error || e?.message || 'Failed to move GD&T row to Dimensions for Review');
    } finally { setDemotingGdt(null); }
  };

  const saveGdtChanges = async () => {
    if (!onSaveGdt) return;
    setSaving(true); setSaveError(null);
    try {
      const numbered = editedRows.map((r, i) => {
        const { _tempId, ...rest } = r;
        return { ...rest, gdt_item_no: i + 1 };
      });
      await onSaveGdt(numbered);
      setEditMode(false);
    } catch (e) {
      setSaveError(e?.error || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const displayRows = editMode ? editedRows : rawRows;
  const count = displayRows.length;

  const gdtBtnBase = { padding: '5px 12px', borderRadius: 5, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12 };

  const fmtDatums = (d) => {
    if (d == null || d === '') return '\u2014';
    if (typeof d === 'string') {
      const fromRepr = lettersFromDatumString(d);
      if (fromRepr.length) return fromRepr.join(', ');
      return d.trim() || '\u2014';
    }
    if (!Array.isArray(d) || d.length === 0) return '\u2014';
    const parts = [];
    for (const entry of d) {
      if (typeof entry === 'string') {
        const fromRepr = lettersFromDatumString(entry);
        if (fromRepr.length) parts.push(...fromRepr);
        else if (entry.trim()) parts.push(entry.trim());
      } else if (entry && typeof entry === 'object' && entry.letter) {
        parts.push(String(entry.letter).trim());
      } else if (entry != null && String(entry).trim()) {
        const fromRepr = lettersFromDatumString(String(entry));
        if (fromRepr.length) parts.push(...fromRepr);
      }
    }
    const joined = parts.filter(Boolean).join(', ');
    return joined || '\u2014';
  };

  /** Feature / Tolerance: FCF zone value (engineering GD&T only). */
  const gdtVal = (r) => {
    const u = r.ucl;
    if (u == null || u === '') return '—';
    const s = String(u).toUpperCase();
    if (s === 'ANOMALY' || s === 'BASIC' || s === 'NA') return '—';
    const n = parseFloat(u);
    return isNaN(n) ? u : String(n);
  };

  const datumDisplayVal = (r) => {
    if (typeof r.datum_references === 'string') return r.datum_references;
    if (Array.isArray(r.datum_references)) return r.datum_references.join(', ');
    return '';
  };

  return (
    <div style={{ marginTop: 32 }} id="gdt-table-section">
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, marginBottom: 12, borderLeft: '4px solid #E36209', paddingLeft: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#E36209' }}>&#8853;</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#9a3412' }}>
              Geometric Dimensioning and Tolerancing (GD&amp;T) Table
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              {count > 0
                ? `${count} Feature Control Frame${count !== 1 ? 's' : ''} extracted from the drawing`
                : 'No GD&T Feature Control Frames detected on this drawing'}
            </div>
          </div>
        </div>
        {onSaveGdt && !editMode && (
          <button style={{ ...gdtBtnBase, background: '#2E75B6', color: '#fff' }} onClick={enterEditMode}>
            &#9998; Edit GD&amp;T
          </button>
        )}
        {editMode && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...gdtBtnBase, background: '#16a34a', color: '#fff' }} onClick={saveGdtChanges} disabled={saving}>
              {saving ? 'Saving…' : '✓ Save'}
            </button>
            <button style={{ ...gdtBtnBase, background: '#1F3864', color: '#fff' }} onClick={addGdtRow} disabled={saving}>
              + Add Row
            </button>
            <button style={{ ...gdtBtnBase, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }} onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {saveError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 14px', color: '#dc2626', fontSize: 13, marginBottom: 8 }}>
          {saveError}
        </div>
      )}

      {count === 0 && !editMode ? (
        <div style={{ padding: '14px 18px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 13, color: '#92400E', fontStyle: 'italic' }}>
          No GD&amp;T Feature Control Frames were detected on this drawing.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #fdba74' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fff7ed' }}>
                {editMode && <th style={{ width: 36, padding: '9px 6px', borderBottom: '1px solid #fdba74' }}></th>}
                {['#', 'Location', 'Feature', 'GD&T Control', 'Symbol', 'Tolerance', 'Datum Ref'].map(h => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#9a3412', whiteSpace: 'nowrap', borderBottom: '1px solid #fdba74' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r, i) => {
                const isCC = r.critical_characteristic === 'Yes';
                const bg = isCC ? '#FCE4D6' : (i % 2 === 0 ? '#fff' : '#fffbeb');
                if (editMode) {
                  const gdtItemNo = r.gdt_item_no || i + 1;
                  const isDemotingRow = demotingGdt === gdtItemNo;
                  return (
                    <tr key={r._tempId || i} style={{ background: bg }}>
                      <td style={{ padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button onClick={() => deleteGdtRow(i)} style={{ background: '#fee2e2', border: 'none', borderRadius: 3, color: '#dc2626', cursor: 'pointer', fontWeight: 700, padding: '2px 7px', fontSize: 13 }}>✕</button>
                        <button onClick={() => duplicateGdtRow(i)} title="Duplicate GD&amp;T row" style={{ marginLeft: 4, background: '#dbeafe', border: 'none', borderRadius: 3, color: '#1d4ed8', cursor: 'pointer', fontWeight: 700, padding: '2px 7px', fontSize: 13 }}>⧉</button>
                        {onDemoteGdt && (
                          <button
                            onClick={() => demoteGdtRow(r, i)}
                            disabled={isDemotingRow}
                            title="Move this GD&T row to Dimensions for Review"
                            style={{ marginLeft: 4, background: '#FEF3C7', border: 'none', borderRadius: 3, color: '#B45309', cursor: 'pointer', fontWeight: 700, padding: '2px 7px', fontSize: 11, opacity: isDemotingRow ? 0.6 : 1 }}
                          >
                            {isDemotingRow ? '…' : '→ Review'}
                          </button>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', color: '#78350f', fontWeight: 600, whiteSpace: 'nowrap' }}>{i + 1}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={r.location || ''} onChange={e => updateRow(i, 'location', e.target.value)} style={INPUT_STYLE} placeholder="Location" />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={r.feature || r.feature_description || ''} onChange={e => updateRow(i, 'feature', e.target.value)} style={{ ...INPUT_STYLE, width: 90 }} placeholder="Feature" />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={r.spec_sub_type || ''} onChange={e => updateRow(i, 'spec_sub_type', e.target.value)} style={SELECT_STYLE}>
                          <option value="">— Select control —</option>
                          {GDT_CONTROL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <select
                          value={r.gdt_symbol || ''}
                          onChange={e => updateRow(i, 'gdt_symbol', e.target.value)}
                          style={{ ...SELECT_STYLE, fontFamily: 'inherit', minWidth: 160 }}
                        >
                          {GDT_SYMBOL_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={r.ucl || ''} onChange={e => updateRow(i, 'ucl', e.target.value)} style={INPUT_STYLE} placeholder="Tolerance" />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={datumDisplayVal(r)} onChange={e => updateRow(i, 'datum_references', e.target.value)} style={INPUT_STYLE} placeholder="e.g. A, B" />
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={i} id={`gdt-row-${r.gdt_item_no || i}`} style={{ background: bg }}>
                    <td style={{ padding: '8px 12px', color: '#78350f', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {r.gdt_item_no || i + 1}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', color: '#475569' }}>
                      {r.location || '—'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'monospace', color: '#1F3864', fontWeight: 600 }}>
                      {gdtVal(r)}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 500 }}>
                      {r.spec_sub_type || '—'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontSize: '1.25em', fontWeight: 700, color: '#1A3D5C' }}>
                      {resolveGlyph(r)
                        || <span style={{ fontSize: '0.65em', fontWeight: 400, fontStyle: 'italic', color: '#94a3b8' }}>Refer drawing</span>}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'monospace', color: '#1F3864' }}>
                      {gdtVal(r)}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'monospace', color: '#475569' }}>
                      {fmtDatums(r.datum_references)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Low Confidence Dimensions — every extracted dimension that did NOT reach the
 * main inspection table (references, basics, anomalies, and rows that would
 * otherwise have been dropped silently). Shows the same columns as the main
 * table plus the vision confidence and the reason it was excluded.
 */
function PromoteModal({ destination, onCancel, onConfirm, prefill }) {
  const isGdt = destination === 'gdt';
  const defaultFields = isGdt
    ? { control_name: '', symbol: '', tolerance: '', datum_references: '', material_condition: '' }
    : { category: 'General Note', note_label: '', value_detail: '', verification_method: 'Visual inspection', responsible_role: 'QC Inspector' };
  const [f, setF] = useState(prefill || defaultFields);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const gdtFields = [['control_name', 'GD&T Control (e.g. Total Runout)'], ['symbol', 'Symbol glyph'], ['tolerance', 'Tolerance'], ['datum_references', 'Datums (comma-sep, e.g. A,B)'], ['material_condition', 'Material condition (MMC/LMC/RFS)']];
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 24, width: 460, maxWidth: '92vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 16, color: '#1e293b' }}>
          Share to {isGdt ? 'GD&T Table' : 'Notes Table'}
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: 12.5, color: '#64748b' }}>
          {isGdt ? 'Fill the GD&T fields so the row fits the destination table.' : 'Review and confirm the auto-detected note fields below.'}
        </p>
        {isGdt ? (
          gdtFields.map(([k, label]) => (
            <div key={k} style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>
              <input value={f[k] || ''} onChange={set(k)} style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          ))
        ) : (
          <>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Category</label>
              <select value={f.category || 'General Note'} onChange={set('category')} style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}>
                {NOTE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Note / Specification</label>
              <input value={f.note_label || ''} onChange={set('note_label')} style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Value / Detail</label>
              <input value={f.value_detail || ''} onChange={set('value_detail')} style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Verification Method</label>
              <input value={f.verification_method || ''} onChange={set('verification_method')} style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Responsible Role</label>
              <input value={f.responsible_role || ''} onChange={set('responsible_role')} style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onCancel} style={{ padding: '8px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button onClick={() => onConfirm(f)} style={{ padding: '8px 16px', border: 'none', background: '#2563eb', color: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Share →</button>
        </div>
      </div>
    </div>
  );
}

function LowConfidenceDimensionsSection({ rows, onPromote }) {
  const data = (Array.isArray(rows) ? rows : []).filter((r) => r && typeof r === 'object');
  const count = data.length;
  const canPromote = typeof onPromote === 'function';
  const [busyIdx, setBusyIdx] = useState(null);
  const [dest, setDest] = useState({});           // per-row selected destination
  const [modal, setModal] = useState(null);        // { lcIndex, destination }
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.reduce((acc, row, originalIndex) => {
      const typeOk =
        typeFilter === 'all' ||
        (row.inspection_type || '').toLowerCase() === typeFilter;
      if (!typeOk) return acc;
      if (q) {
        const haystack = [
          row.item_no,
          row.location_on_drawing,
          row.location,
          row.table_reference,
          row.specification,
          row.inspection_type,
          row.spec_category,
          row.spec_sub_type,
          CAT_LABELS[row.spec_category] || '',
          row.inspection_tool,
          row.exclusion_reason,
          row.exclusion_reason_label,
          row.critical_characteristic,
        ].map((v) => String(v ?? '').toLowerCase()).join(' ');
        if (!haystack.includes(q)) return acc;
      }
      acc.push({ row, originalIndex });
      return acc;
    }, []);
  }, [data, search, typeFilter]);

  // "Belongs in the MAIN table" likelihood per review row — reviewers triage
  // high-chance rows first. Derived from HOW the row was excluded.
  const mainChance = (row) => {
    if (row.likely_real) return 0.75;                       // bare, unverifiable — often real
    const reason = String(row.exclusion_reason || '') + ' ' +
                   String(row.exclusion_reason_label || '');
    const rl = reason.toLowerCase();
    if (rl.includes('basic')) return 0.15;                  // boxed basic — correctly excluded
    if (rl.includes('reference') || rl.includes('paren')) return 0.05;
    const c = Number(row.confidence);
    if (Number.isFinite(c) && c < 0.7) return 0.4;          // weak extraction — could be real
    return 0.3;
  };
  const sortedRows = useMemo(
    () => [...filteredRows].sort((a, b) => mainChance(b.row) - mainChance(a.row)),
    [filteredRows]
  );

  const filteredCount = filteredRows.length;
  const isFiltered = search.trim() !== '' || typeFilter !== 'all';

  const doPromote = async (lcIndex, destination, overrides) => {
    setBusyIdx(lcIndex); setErr(null);
    try {
      await onPromote(lcIndex, destination, overrides || {});
    } catch (e) {
      setErr(e?.error || e?.message || 'Failed to share row');
    } finally {
      setBusyIdx(null); setModal(null);
    }
  };

  const onShareClick = (lcIndex, row) => {
    const destination = dest[lcIndex] || 'dimension';
    if (destination === 'dimension') doPromote(lcIndex, 'dimension', {});
    else {
      const prefill = destination === 'note' ? autoNoteFields(row) : null;
      setModal({ lcIndex, destination, prefill });
    }
  };

  const fmtNum = (v) => {
    if (v == null || v === '') return '\u2014';
    const n = parseFloat(v);
    return isNaN(n) ? String(v) : String(n);
  };
  const fmtLimit = (v) => {
    if (v == null || v === '') return '\u2014';
    return String(v);
  };
  const catLabel = (r) => {
    const c = r.spec_category;
    const lbl = CAT_LABELS[c] || c || '\u2014';
    return r.spec_sub_type ? `${lbl} / ${r.spec_sub_type}` : lbl;
  };

  const hdrs = [
    '#', 'Location', 'CC', 'Specification', 'Tol (+)', 'Tol (\u2212)',
    'UCL', 'LCL', 'Specification Type', 'Specification Category',
    'Inspection Tool', 'Confidence', 'Reason Excluded',
    ...(canPromote ? ['Share \u2192'] : []),
  ];

  return (
    <div style={{ marginTop: 32 }} id="low-confidence-dimensions-section">
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
        borderLeft: '4px solid #B45309', paddingLeft: 12,
      }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#B45309' }}>⚠</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#92400E' }}>
            Dimensions for Review
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {count > 0
              ? isFiltered
                ? `Showing ${filteredCount} of ${count} dimension${count !== 1 ? 's' : ''} pending review`
                : `${count} dimension${count !== 1 ? 's' : ''} pending review — confirm or promote each to the Specification Table`
              : 'All extracted dimensions were placed on the Specification Table'}
          </div>
        </div>
      </div>

      {count > 0 && (
        <div className="table-tools" style={{ marginBottom: 12 }}>
          <input
            type="text"
            className="table-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by item, spec, type, tool, reason, or classification…"
          />
          <select
            className="table-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="dimensional">Dimensional</option>
            <option value="visual">Visual</option>
          </select>
        </div>
      )}

      {count === 0 ? (
        <div style={{
          padding: '14px 18px', background: '#FFFBEB', border: '1px solid #FDE68A',
          borderRadius: 8, fontSize: 13, color: '#92400E', fontStyle: 'italic',
        }}>
          No dimensions were dropped or excluded from the Specification Table.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #FCD34D' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#FFFBEB' }}>
                {hdrs.map((h) => (
                  <th key={h} style={{
                    padding: '9px 12px', textAlign: 'left', fontWeight: 600,
                    fontSize: 11, color: '#92400E', whiteSpace: 'nowrap',
                    borderBottom: '1px solid #FCD34D',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.length > 0 ? sortedRows.map(({ row: r, originalIndex: i }, visIdx) => {
                const bg = visIdx % 2 === 0 ? '#fff' : '#FFFCF2';
                const conf = typeof r.confidence === 'number' ? r.confidence : null;
                const lowConf = conf != null && conf < 0.85;
                return (
                  <tr key={i} style={{ background: bg }}>
                    <td style={{ padding: '8px 12px', color: '#78350f', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {r.item_no || i + 1}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', color: '#475569' }}>
                      {r.location_on_drawing || r.location || '\u2014'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', color: '#475569' }}>
                      {r.critical_characteristic === 'Yes' ? 'Yes' : 'No'}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 500 }}>
                      {r.specification || '\u2014'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'monospace', color: '#475569' }}>{fmtTolPlus(r.tolerance_plus)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'monospace', color: '#475569' }}>{fmtTolMinus(r.tolerance_minus)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'monospace', color: '#475569' }}>{fmtLimit(r.ucl)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'monospace', color: '#475569' }}>{fmtLimit(r.lcl)}</td>
                    <td style={{ padding: '8px 12px', color: '#475569' }}>{r.inspection_type || '\u2014'}</td>
                    <td style={{ padding: '8px 12px', color: '#475569' }}>{catLabel(r)}</td>
                    <td style={{ padding: '8px 12px', color: '#475569' }}>{r.inspection_tool || '\u2014'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {conf != null ? (
                        <span style={{
                          fontWeight: 600,
                          color: lowConf ? '#B45309' : '#15803d',
                          background: lowConf ? '#FEF3C7' : '#dcfce7',
                          borderRadius: 6, padding: '2px 8px', fontSize: 12,
                        }}>
                          {Math.round(conf * 100)}%
                        </span>
                      ) : '\u2014'}
                    </td>
                    <td style={{ padding: '8px 12px', color: r.likely_real ? '#1D4ED8' : '#92400E', fontSize: 12 }}>
                      {(() => {
                        const mc = mainChance(r);
                        const hi = mc >= 0.6, mid = mc >= 0.3 && mc < 0.6;
                        return (
                          <span
                            title="Likelihood this row belongs in the Specification Table"
                            style={{
                              background: hi ? '#DBEAFE' : mid ? '#FEF3C7' : '#F1F5F9',
                              color: hi ? '#1D4ED8' : mid ? '#B45309' : '#64748B',
                              border: '1px solid ' + (hi ? '#93C5FD' : mid ? '#FCD34D' : '#CBD5E1'),
                              borderRadius: 6, padding: '1px 8px', fontSize: 11,
                              fontWeight: 700, marginRight: 6, whiteSpace: 'nowrap',
                            }}
                          >
                            {Math.round(mc * 100)}% main-table
                          </span>
                        );
                      })()}
                      {r.exclusion_reason_label || r.exclusion_reason || 'Excluded from the Specification Table.'}
                    </td>
                    {canPromote && (
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <select
                            value={dest[i] || 'dimension'}
                            onChange={(e) => setDest((p) => ({ ...p, [i]: e.target.value }))}
                            style={{ padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 5, fontSize: 12 }}
                          >
                            <option value="dimension">Specification Table</option>
                            <option value="gdt">GD&amp;T</option>
                            <option value="note">Notes</option>
                          </select>
                          <button
                            onClick={() => onShareClick(i, r)}
                            disabled={busyIdx === i}
                            style={{ padding: '4px 10px', border: 'none', background: '#2563eb', color: '#fff', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: busyIdx === i ? 0.6 : 1 }}
                          >
                            {busyIdx === i ? '…' : 'Move'}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              }) : (
                <tr>
                  <td
                    colSpan={hdrs.length}
                    style={{ padding: '14px 12px', textAlign: 'center', color: '#92400E', fontStyle: 'italic' }}
                  >
                    No dimensions for review match your search/filter
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {err && (
        <div style={{ marginTop: 8, color: '#b91c1c', fontSize: 12 }}>{err}</div>
      )}
      {modal && (
        <PromoteModal
          destination={modal.destination}
          prefill={modal.prefill}
          onConfirm={(fields) => doPromote(modal.lcIndex, modal.destination, fields)}
        />
      )}
    </div>
  );
}

const ResultsTable = ({ metadata, items, notesItems, visualInspectionItems, safetyHazardItems, gdtTableRows, gdtSymbolsUnused, lowConfidenceDimensions, onPromoteLowConfidence, onDemoteDimension, onSaveItems, onSaveNotes: onSaveNotesProp, onSaveGdt: onSaveGdtProp, onDemoteNote: onDemoteNoteProp, onDemoteGdt: onDemoteGdtProp, ccCount, itemCount, fileId, onSaved, blockTolerances, scanDurationMs }) => {
  // notesItems is the v2 Notes Table; safetyHazardItems kept for backward compat.
  // Prefer notesItems; fall back to safetyHazardItems for old scan results.
  const rawNotesItems = (notesItems && notesItems.length > 0) ? notesItems : (safetyHazardItems || []);

  // Visual Inspection sub-table.
  // - If the API already returns visual_inspection_items (new scans / freshly
  //   redeployed backend), trust it and render rawNotesItems unchanged.
  // - Otherwise, derive both lists client-side so older scans still split
  //   correctly without a backend redeploy.
  const { resolvedNotesItems, resolvedVisualItems } = useMemo(() => {
    if (Array.isArray(visualInspectionItems)) {
      return {
        resolvedNotesItems: rawNotesItems,
        resolvedVisualItems: visualInspectionItems,
      };
    }
    const split = splitVisualFromNotes(rawNotesItems);
    return {
      resolvedNotesItems: split.notesItems,
      resolvedVisualItems: split.visualItems,
    };
  }, [rawNotesItems, visualInspectionItems]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [editMode, setEditMode] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [editMeta, setEditMeta] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [customTools, setCustomTools] = useState([]);

  useEffect(() => {
    API.getCustomTools().then(setCustomTools).catch(() => { });
  }, []);

  const normalizedItems = useMemo(() => items || [], [items]);
  // "Reference" column (tabular scans only): the table symbol/column each
  // dimension maps to. Shown only when at least one row carries it, so normal
  // scans are unaffected.
  const hasTableReference = useMemo(
    () => normalizedItems.some((i) => i && i.table_reference),
    [normalizedItems],
  );

  const enterEditMode = useCallback(() => {
    setEditedItems(normalizedItems.map(item => ({ ...item })));
    setEditMeta({ ...(metadata || {}) });
    setSaveError(null);
    setEditMode(true);
  }, [normalizedItems, metadata]);

  const cancelEdit = useCallback(() => {
    setEditMode(false);
    setSaveError(null);
  }, []);

  const updateCell = useCallback((rowIdx, field, value) => {
    setEditedItems(prev => prev.map((row, i) => i === rowIdx ? { ...row, [field]: value } : row));
  }, []);

  const addRow = useCallback(() => {
    setEditedItems(prev => [...prev, BLANK_ROW()]);
  }, []);

  const deleteRow = useCallback((rowIdx) => {
    setEditedItems(prev => prev.filter((_, i) => i !== rowIdx));
  }, []);

  const duplicateRow = useCallback((rowIdx) => {
    setEditedItems(prev => {
      const next = [...prev];
      next.splice(rowIdx + 1, 0, { ...prev[rowIdx], _tempId: Math.random().toString(36).slice(2) });
      return next;
    });
  }, []);

  // Move a dimension from the main table to Low Confidence (reverse of promote).
  const [demotingSpec, setDemotingSpec] = useState(null);
  const handleDemote = useCallback(async (item) => {
    if (!onDemoteDimension) return;
    const spec = item.specification;
    setDemotingSpec(spec);
    setSaveError(null);
    try {
      await onDemoteDimension(spec, item.location_on_drawing || item.location || '');
      // Reflect immediately in the edit view; parent refetch updates the LC table.
      setEditedItems(prev => prev.filter(r => r.specification !== spec));
    } catch (e) {
      setSaveError(e?.error || e?.message || 'Failed to move dimension to Dimensions for Review');
    } finally {
      setDemotingSpec(null);
    }
  }, [onDemoteDimension]);

  const saveChanges = useCallback(async () => {
    if (!fileId) { setSaveError('No file ID — cannot save.'); return; }
    setSaving(true);
    setSaveError(null);
    try {
      // Re-number items before saving
      const numbered = editedItems.map((item, idx) => {
        const { _tempId, ...rest } = item;
        return { ...rest, item_no: idx + 1 };
      });
      if (onSaveItems) {
        // Tabular scans: the plan lives inside the selected variant, so the
        // parent decides where to persist (PATCH /tabular/{id}/{index}).
        await onSaveItems(numbered, editMeta);
      } else {
        await API.updateResults(fileId, {
          inspection_items: numbered,
          metadata: editMeta,
        });
      }
      // Bust dashboard caches so the history row and stat cards reflect the
      // new row count on the next load (the detail modal is already live).
      try { API.invalidateCache('history', 'stats'); } catch {}
      setEditMode(false);
      if (onSaved) onSaved();
    } catch (err) {
      setSaveError(err.error || err.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [fileId, editedItems, editMeta, onSaved, onSaveItems]);

  const handleSaveNotes = useCallback(async (editedNotes) => {
    if (!fileId) return;
    await API.updateResults(fileId, { notes_items: editedNotes });
    if (onSaved) onSaved();
  }, [fileId, onSaved]);

  const handleSaveGdt = useCallback(async (editedGdt) => {
    if (!fileId) return;
    await API.updateResults(fileId, { gdt_table_rows: editedGdt });
    if (onSaved) onSaved();
  }, [fileId, onSaved]);

  const handleDemoteNote = useCallback(async (noteLabel, category) => {
    if (!fileId) return;
    await API.demoteResultsNote(fileId, noteLabel, category);
    if (onSaved) onSaved();
  }, [fileId, onSaved]);

  const handleDemoteGdt = useCallback(async (gdtItemNo) => {
    if (!fileId) return;
    await API.demoteResultsGdt(fileId, gdtItemNo);
    if (onSaved) onSaved();
  }, [fileId, onSaved]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return normalizedItems.filter((item) => {
      const typeOk =
        typeFilter === 'all' ||
        (item.inspection_type || '').toLowerCase() === typeFilter;
      if (!typeOk) return false;
      if (!q) return true;
      const haystack = [
        item.item_no, item.location_on_drawing, item.specification,
        item.inspection_type, item.spec_category, item.spec_sub_type,
        CAT_LABELS[item.spec_category] || '', item.inspection_tool,
      ].map(v => String(v ?? '').toLowerCase()).join(' ');
      return haystack.includes(q);
    });
  }, [normalizedItems, search, typeFilter]);

  const formatLimit = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'string' && value.toUpperCase() === 'BASIC') return '—';
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(4) : String(value);
  };

  // ── Edit mode toolbar styles ────────────────────────────────────────────────
  const btnBase = {
    padding: '6px 14px', borderRadius: 5, border: 'none',
    cursor: 'pointer', fontWeight: 600, fontSize: 13,
  };

  return (
    <div className="results-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        <h2 style={{ margin: 0 }}>Inspection Plan Results</h2>
        {fileId && !editMode && (
          <button
            style={{ ...btnBase, background: '#2E75B6', color: '#fff' }}
            onClick={enterEditMode}
          >
            ✏ Edit Results
          </button>
        )}
        {editMode && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              style={{ ...btnBase, background: '#16a34a', color: '#fff' }}
              onClick={saveChanges}
              disabled={saving}
            >
              {saving ? 'Saving…' : '✓ Save Changes'}
            </button>
            <button
              style={{ ...btnBase, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              style={{ ...btnBase, background: '#1F3864', color: '#fff' }}
              onClick={addRow}
              disabled={saving}
            >
              + Add Row
            </button>
          </div>
        )}
      </div>

      {saveError && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6,
          padding: '8px 14px', color: '#dc2626', fontSize: 13, marginBottom: 8,
        }}>
          {saveError}
        </div>
      )}

      {/* ── Metadata cards (editable in edit mode) ─────────────────────── */}
      <div className="metadata-cards">
        {[
          ['Part No', 'part_no'],
          ['Drawing No', 'drawing_no'],
          ['Part Name', 'part_name'],
          ['Rev No', 'rev_no'],
        ].map(([label, key]) => (
          <div className="metadata-card" key={key}>
            <label>{label}</label>
            {editMode ? (
              <input
                style={{ ...INPUT_STYLE, marginTop: 2, fontWeight: 600 }}
                value={editMeta[key] || ''}
                onChange={e => setEditMeta(prev => ({ ...prev, [key]: e.target.value }))}
              />
            ) : (
              <value>{metadata?.[key] || '—'}</value>
            )}
          </div>
        ))}
        {/* Scan Time card intentionally not shown in results metadata */}
      </div>

      {blockTolerances !== undefined && (
        <BlockTolerancesSection
          blockTolerances={blockTolerances}
          fileId={fileId}
          onSaved={onSaved}
        />
      )}

      {/* ── Section title ─────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, margin: '24px 0 12px',
        borderLeft: '4px solid #F97316', paddingLeft: 12,
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#1F2430' }}>
            Specification Table
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            Every inspectable dimension with tolerances, control limits and assigned tools
          </div>
        </div>
      </div>

      <div className="metrics">
        <div className="metric">
          <div className="metric-value">{editMode ? editedItems.filter(i => i.critical_characteristic === 'Yes').length : (ccCount || 0)}</div>
          <div className="metric-label">CC Symbols</div>
        </div>
        <div className="metric">
          <div className="metric-value">{editMode ? editedItems.length : (itemCount || 0)}</div>
          <div className="metric-label">Items</div>
        </div>
        <div className="metric">
          <div className="metric-value">
            {(editMode ? editedItems : normalizedItems).filter(i => i.inspection_type === 'Dimensional').length || 0}
          </div>
          <div className="metric-label">Dimensional</div>
        </div>
        <div className="metric">
          <div className="metric-value">
            {(editMode ? editedItems : normalizedItems).filter(i => i.inspection_type === 'Visual').length || 0}
          </div>
          <div className="metric-label">Visual</div>
        </div>
      </div>

      {/* ── Search/filter (hidden in edit mode to reduce clutter) ──────── */}
      {!editMode && (
        <div className="table-tools">
          <input
            type="text"
            className="table-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by item, spec, type, tool, or classification…"
          />
          <select
            className="table-filter"
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="dimensional">Dimensional</option>
            <option value="visual">Visual</option>
          </select>
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div className="table-container">
        <table className="results-table">
          <thead>
            <tr>
              {editMode && <th style={{ width: 36 }}></th>}
              <th>Item No</th>
              <th>Location</th>
              {hasTableReference && <th>Reference</th>}
              <th>CC</th>
              <th>Specification</th>
              <th>Tol (+)</th>
              <th>Tol (−)</th>
              <th>UCL</th>
              <th>LCL</th>
              <th>Specification Type</th>
              <th>Specification Category</th>
              <th>Tool</th>
              <th>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {editMode ? (
              editedItems.length > 0 ? editedItems.map((item, idx) => (
                <tr key={item._tempId || idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  {/* Row actions: delete + move to Low Confidence */}
                  <td style={{ textAlign: 'center', padding: '4px 2px', whiteSpace: 'nowrap' }}>
                    <button
                      title="Delete row"
                      onClick={() => deleteRow(idx)}
                      style={{
                        background: '#fee2e2', border: 'none', borderRadius: 3,
                        color: '#dc2626', cursor: 'pointer', fontWeight: 700,
                        padding: '2px 7px', fontSize: 13, lineHeight: 1, marginRight: 4,
                      }}
                    >
                      ✕
                    </button>
                    <button
                      title="Duplicate row"
                      onClick={() => duplicateRow(idx)}
                      style={{
                        background: '#dbeafe', border: 'none', borderRadius: 3,
                        color: '#1d4ed8', cursor: 'pointer', fontWeight: 700,
                        padding: '2px 7px', fontSize: 13, lineHeight: 1, marginRight: 4,
                      }}
                    >
                      ⧉
                    </button>
                    {onDemoteDimension && (
                      <button
                        title="Move this dimension to Dimensions for Review"
                        onClick={() => handleDemote(item)}
                        disabled={demotingSpec === item.specification}
                        style={{
                          background: '#FEF3C7', border: 'none', borderRadius: 3,
                          color: '#B45309', cursor: 'pointer', fontWeight: 700,
                          padding: '2px 7px', fontSize: 11, lineHeight: 1,
                        }}
                      >
                        {demotingSpec === item.specification ? '…' : '→ LC'}
                      </button>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 600, color: '#64748b', fontSize: 12 }}>{idx + 1}</td>
                  <td><EditCell value={item.location_on_drawing} onChange={v => updateCell(idx, 'location_on_drawing', v)} /></td>
                  {hasTableReference && <td style={{ textAlign: 'center', fontWeight: 600, color: '#2563eb' }}>{item.table_reference || '—'}</td>}
                  <td>
                    <EditCell
                      value={item.critical_characteristic}
                      onChange={v => updateCell(idx, 'critical_characteristic', v)}
                      options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]}
                    />
                  </td>
                  <td><EditCell value={item.specification} onChange={v => updateCell(idx, 'specification', v)} /></td>
                  <td><EditCell value={item.tolerance_plus ?? ''} onChange={v => updateCell(idx, 'tolerance_plus', v)} type="number" /></td>
                  <td><EditCell value={item.tolerance_minus ?? ''} onChange={v => updateCell(idx, 'tolerance_minus', v)} type="number" /></td>
                  <td><EditCell value={item.ucl ?? ''} onChange={v => updateCell(idx, 'ucl', v)} type="number" /></td>
                  <td><EditCell value={item.lcl ?? ''} onChange={v => updateCell(idx, 'lcl', v)} type="number" /></td>
                  <td>
                    <select
                      value={item.inspection_type || 'Dimensional'}
                      onChange={e => updateCell(idx, 'inspection_type', e.target.value)}
                      style={SELECT_STYLE}
                    >
                      <option value="Dimensional">Dimensional</option>
                      <option value="Visual">Visual</option>
                    </select>
                  </td>
                  {/* Specification Category — category + sub-type linked dropdowns */}
                  <td>
                    <select
                      value={item.spec_category || ''}
                      onChange={e => {
                        const v = e.target.value;
                        const cat = CAT_DATA_MAP[v];
                        setEditedItems(prev => prev.map((row, i) => i !== idx ? row : {
                          ...row,
                          spec_category: v,
                          spec_sub_type: '',
                          inspection_tool: '',
                          inspection_type: cat ? (cat.spec_type === 'dimensional' ? 'Dimensional' : 'Visual') : row.inspection_type,
                        }));
                      }}
                      style={SELECT_STYLE}
                    >
                      <option value="">— Select type —</option>
                      {CATEGORIES_DATA.map(c => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>
                    {item.spec_category && CAT_DATA_MAP[item.spec_category] && (
                      <select
                        value={item.spec_sub_type || ''}
                        onChange={e => updateCell(idx, 'spec_sub_type', e.target.value)}
                        style={{ ...SELECT_STYLE, marginTop: 4 }}
                      >
                        <option value="">— Sub-type —</option>
                        {CAT_DATA_MAP[item.spec_category].sub_types.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  {/* Tool — dropdown filtered to selected category tools + custom tools */}
                  <td>
                    {item.spec_category && CAT_DATA_MAP[item.spec_category] ? (
                      <select
                        value={item.inspection_tool || ''}
                        onChange={e => updateCell(idx, 'inspection_tool', e.target.value)}
                        style={SELECT_STYLE}
                      >
                        <option value="">— Select tool —</option>
                        <optgroup label="Standard Tools">
                          {CAT_DATA_MAP[item.spec_category].tools.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </optgroup>
                        {customTools.length > 0 && (
                          <optgroup label="Custom Tools">
                            {customTools.map(ct => (
                              <option key={ct.id} value={ct.tool_name}>{ct.tool_name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    ) : (
                      <select
                        value={item.inspection_tool || ''}
                        onChange={e => updateCell(idx, 'inspection_tool', e.target.value)}
                        style={SELECT_STYLE}
                      >
                        <option value="">— Select tool —</option>
                        {customTools.length > 0 && (
                          <optgroup label="Custom Tools">
                            {customTools.map(ct => (
                              <option key={ct.id} value={ct.tool_name}>{ct.tool_name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    )}
                  </td>
                  {/* Confidence — read-only in edit mode */}
                  <td style={{ textAlign: 'center', color: '#64748b', fontSize: 12 }}>
                    {item.confidence != null ? `${Math.round(item.confidence * 100)}%` : '—'}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="13" style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No rows — click "+ Add Row" to add one.</td></tr>
              )
            ) : (
              filteredItems.length > 0 ? (
                filteredItems.map((item, idx) => (
                  <tr key={`${item.item_no}-${idx}`} id={`dim-row-${item.item_no}`}>
                    <td>{idx + 1}</td>
                    <td>{item.location_on_drawing || item.location || '—'}</td>
                    {hasTableReference && <td style={{ textAlign: 'center', fontWeight: 600, color: '#2563eb' }}>{item.table_reference || '—'}</td>}
                    <td>
                      <span className={`cc-badge ${item.critical_characteristic === 'Yes' ? 'yes' : 'no'}`}>
                        {item.critical_characteristic}
                      </span>
                    </td>
                    <td>{item.specification || '—'}</td>
                    <td>{fmtTolPlus(item.tolerance_plus)}</td>
                    <td>{fmtTolMinus(item.tolerance_minus)}</td>
                    <td>{formatLimit(item.ucl)}</td>
                    <td>{formatLimit(item.lcl)}</td>
                    <td><SpecificationTypeCell item={item} /></td>
                    <td style={{ maxWidth: 220 }}>{specCategoryDisplay(item)}</td>
                    <td>
                      {item.inspection_tool || '—'}
                      {item.tool_source === 'model' && (
                        <span
                          title={item.tool_rationale || 'Selected by the model'}
                          style={{
                            fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                            background: '#dbeafe', color: '#1d4ed8', marginLeft: 5,
                            textTransform: 'uppercase', letterSpacing: .4, verticalAlign: 'middle',
                          }}
                        >AI</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {(() => {
                        // No stored value → derive: decorated specs (±/Ø/R/°/X)
                        // cannot be misread basics → high; bare numbers passed
                        // pixel verification → slightly lower.
                        const spec = String(item.specification || '');
                        const derived = /[±°ØøΦφ⌀]|\bR\s?\.?\d|\d\s*[xX]\s*[ØøR\d.]|[+/]/.test(spec) ? 0.95 : 0.85;
                        const conf = item.confidence != null ? item.confidence : derived;
                        return (
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 12,
                              color: conf < 0.85 ? '#b45309' : '#15803d',
                              background: conf < 0.85 ? '#fef3c7' : '#dcfce7',
                              padding: '2px 7px',
                              borderRadius: 4,
                            }}
                            title={conf < 0.85 ? 'Lower confidence — verify this dimension manually' : 'High confidence — passed all classification gates'}
                          >
                            {Math.round(conf * 100)}%
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="13">No inspection items match your search/filter</td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {/* ── Bottom action bar (edit mode only) ─────────────────────────── */}
      {editMode && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
          <button
            style={{ ...btnBase, background: '#1F3864', color: '#fff' }}
            onClick={addRow}
            disabled={saving}
          >
            + Add Row
          </button>
          <button
            style={{ ...btnBase, background: '#16a34a', color: '#fff' }}
            onClick={saveChanges}
            disabled={saving}
          >
            {saving ? 'Saving…' : '✓ Save Changes'}
          </button>
          <button
            style={{ ...btnBase, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}
            onClick={cancelEdit}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── GD&T Table ──────────────────────────────────────────────────── */}
      <GdtTableSection
        gdtRows={gdtTableRows}
        onSaveGdt={onSaveGdtProp || (fileId ? handleSaveGdt : undefined)}
        onDemoteGdt={onDemoteGdtProp || (fileId ? handleDemoteGdt : undefined)}
      />

      {/* ── Combined Notes and Visual Inspection section ─────────────────── */}
      <NotesAndVisualSection
        visualItems={resolvedVisualItems}
        notesItems={resolvedNotesItems}
        onSaveNotes={onSaveNotesProp || (fileId ? handleSaveNotes : undefined)}
        onDemoteNote={onDemoteNoteProp || (fileId ? handleDemoteNote : undefined)}
      />

      {/* ── Dimensions for Review — shown after Notes ──────────────── */}
      <LowConfidenceDimensionsSection rows={lowConfidenceDimensions} onPromote={onPromoteLowConfidence} />
    </div>
  );
};

export default ResultsTable;